import {
  Dispatch,
  MouseEvent as ReactMouseEvent,
  ReactNode,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { createPortal } from 'react-dom';
import { Drawer } from '@mantine/core';
import commonStore from '../../../../store/common';
import { StyledWrapper } from './style';
import { observer } from 'mobx-react-lite';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import authStore from '../../../../store/auth';
import { useGetSourceData } from '../../../../services/report.service';
import { NumericFormat } from 'react-number-format';
import { idCreator } from '../../../Project/utils/idCreator';

interface DrillDownData {
  table_id?: string | number;
  column_name?: string;
  table_name?: string;
  report_id?: string | number;
  fund_id?: string | number;
  column_pretty_name?: string;
}

const fetchStreamedData = async (
  data: DrillDownData,
  setDataCallback: Dispatch<SetStateAction<string>>,
  controller: AbortController,
  token: string | null
): Promise<void> => {
  try {
    const response = await fetch(`${import.meta.env.VITE_BASE_URL}api/drill-down/explain`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        ...data,
        table_name: data?.table_name?.includes('INDIVIDUAL_EXPOSURES')
          ? 'INDIVIDUAL_EXPOSURES'
          : data?.table_name,
        metadata_included: true
      }),
      signal: controller.signal
    });

    const contentType = response.headers.get('Content-Type') || '';
    if (contentType.includes('application/json')) {
      const res = await response.json();
      if (res.explanation_text) {
        setDataCallback(res.explanation_text);
        return;
      }
    }

    // @ts-ignore
    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');

    while (true) {
      const { done, value } = await reader.read();

      if (done) break;
      setDataCallback(prev => prev + decoder.decode(value));
    }
  } catch (e) {
    console.log(e);
  }
};

export const DRILL_DOWN_DRAWER_WIDTH = 500;
export const MIN_DRILL_DOWN_DRAWER_WIDTH = 360;

interface DrillDownProps {
  id?: string;
}

export const DrillDown = observer(({ id }: DrillDownProps) => {
  const [streamedData, setStreamedData] = useState('');
  const [displayText, setDisplayText] = useState('');
  const [index, setIndex] = useState(0);
  const CHUNK_SIZE = 10;
  const controllerRef = useRef<AbortController | null>(null);
  const token = authStore.token;
  const drillDown = commonStore.drillDown as DrillDownData | null;

  const tableData = useGetSourceData({
    params: {
      table_id: drillDown?.table_id,
      column_name: drillDown?.column_name,
      table_name: drillDown?.table_name?.includes('INDIVIDUAL_EXPOSURES')
        ? 'INDIVIDUAL_EXPOSURES'
        : drillDown?.table_name,
      report_id: drillDown?.report_id,
      metadata_included: false
    },
    queryParams: {
      enabled: !!drillDown?.table_id
    }
  });

  useEffect(() => {
    controllerRef.current = new AbortController();
    if (drillDown) {
      setIndex(0);
      setStreamedData('');
      setDisplayText('');
      fetchStreamedData(drillDown, setStreamedData, controllerRef.current!, token);
    }

    return () => {
      controllerRef.current?.abort();
    };
  }, [drillDown, token]);

  useEffect(() => {
    if (index < streamedData.length) {
      const chunk = streamedData.slice(index, index + CHUNK_SIZE);
      const typingEffect = setTimeout(() => {
        setDisplayText(prev => prev + chunk);
        setIndex(prevIndex => prevIndex + CHUNK_SIZE);
      }, 50);
      return () => clearTimeout(typingEffect);
    }
  }, [index, streamedData]);

  const onClose = () => {
    commonStore.setDrillDown(null);
    setIndex(0);
    setStreamedData('');
    setDisplayText('');

    if (controllerRef.current) {
      controllerRef.current?.abort();
      controllerRef.current = null;
    }
  };

  const drillDownWidth = commonStore.drillDownWidth;

  const handleResizeStart = (event: ReactMouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    const startX = event.clientX;
    const startWidth = commonStore.drillDownWidth;

    let frame = 0;
    let latestX = startX;

    const apply = () => {
      frame = 0;
      const next = startWidth + (startX - latestX);
      const maxWidth = window.innerWidth - 200;
      commonStore.setDrillDownWidth(
        Math.min(maxWidth, Math.max(MIN_DRILL_DOWN_DRAWER_WIDTH, next))
      );
    };

    const handleMove = (moveEvent: globalThis.MouseEvent) => {
      // Drawer is on the right, so dragging left (smaller clientX) widens it.
      // Coalesce the high-frequency mousemoves into one store update per frame.
      latestX = moveEvent.clientX;
      if (!frame) frame = requestAnimationFrame(apply);
    };

    const handleUp = () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
      if (frame) cancelAnimationFrame(frame);
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    document.body.style.userSelect = 'none';
  };

  const isOpen = !!drillDown;

  const sourceData = tableData?.data;

  // Memoize the heavy content so resizing the drawer (which re-renders this
  // observer) doesn't re-parse the markdown or rebuild the source tables.
  const markdownContent = useMemo(
    () => (
      <ReactMarkdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
        {displayText}
      </ReactMarkdown>
    ),
    [displayText]
  );

  const tablesContent = useMemo(
    () =>
      Object.keys(sourceData || {}).map(tableName => {
        // @ts-ignore
        const tableDataItems = sourceData[tableName];
        return (
          <div
            key={tableName}
            className="table-container"
            id={`drill-down-table-container-${idCreator(tableName)}`}
          >
            <p className="table-title" id={`drill-down-table-title-${idCreator(tableName)}`}>
              {tableName}
            </p>
            {tableDataItems?.length > 0 && (
              <div className="table" id={`drill-down-table-wrap-${idCreator(tableName)}`}>
                <table id={`drill-down-table-${idCreator(tableName)}`}>
                  <thead>
                    <tr id={`drill-down-table-head-row-${idCreator(tableName)}`}>
                      <th>#</th>
                      {Object.values(tableDataItems[0] as Record<string, string>).map(value => (
                        <th key={value}>{value}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tableDataItems.slice(1).map((item: Record<string, unknown>, idx: number) => (
                      <tr
                        key={idx + 'tr'}
                        id={`drill-down-table-row-${idCreator(tableName)}-${idx}`}
                      >
                        <td>{idx + 1}</td>
                        {Object.values(item).map((value, j) => {
                          if (typeof value === 'number')
                            return (
                              <td
                                key={`${idx}${j}-td`}
                                id={`drill-down-table-cell-${idCreator(tableName)}-${idx}-${j}`}
                              >
                                <NumericFormat
                                  displayType="text"
                                  thousandSeparator=","
                                  value={value}
                                />
                              </td>
                            );
                          return (
                            <td
                              key={`${idx}${j}-td`}
                              id={`drill-down-table-cell-${idCreator(tableName)}-${idx}-${j}`}
                            >
                              {value as ReactNode}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      }),
    [sourceData]
  );

  return (
    <>
      {isOpen &&
        createPortal(
          <div
            id="drill-down-resize-handle"
            onMouseDown={handleResizeStart}
            style={{
              position: 'fixed',
              top: 0,
              bottom: 0,
              right: drillDownWidth - 3,
              width: 6,
              cursor: 'ew-resize',
              zIndex: 10000
            }}
          />,
          document.body
        )}
      <Drawer
        id={id ?? 'drill-down-drawer'}
        title={drillDown?.column_pretty_name}
        opened={isOpen}
        onClose={onClose}
        position="right"
        size={drillDownWidth}
        withOverlay={false}
        lockScroll={false}
        trapFocus={false}
        closeOnClickOutside={false}
        zIndex={9999}
      >
        <StyledWrapper id="drill-down-wrapper">
          <div className="markdown-content" id="drill-down-markdown-content">
            {markdownContent}
            {index < streamedData.length && (
              <span className="cursor" id="drill-down-cursor">
                |
              </span>
            )}
            {tablesContent}
          </div>
        </StyledWrapper>
      </Drawer>
    </>
  );
});
